import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, Check, X, Pencil, ExternalLink } from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { card, input, btnPrimary } from './theme';

/**
 * Cite-Bait Posts — the shipped-content source of truth Hunter checks every
 * hit against ("does this overlap a live post?") and the weekly digest uses
 * to flag duplicate topic candidates. Same fast inline-table pattern as
 * Keyword Library: add/edit/delete, no modals.
 */
export default function CiteBaitPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [newUrl, setNewUrl] = useState('');
  const [newTopic, setNewTopic] = useState('');

  const [editUrl, setEditUrl] = useState('');
  const [editTopic, setEditTopic] = useState('');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hunter_citebait_posts')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setPosts(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const addPost = async () => {
    const url = newUrl.trim();
    const topic = newTopic.trim();
    if (!url || !topic) return;
    setSaving(true);
    const { data, error } = await supabase
      .from('hunter_citebait_posts')
      .insert({ url, topic })
      .select()
      .single();
    if (!error && data) {
      setPosts((prev) => [data, ...prev]);
      setNewUrl('');
      setNewTopic('');
    }
    setSaving(false);
  };

  const deletePost = async (id) => {
    setBusyId(id);
    const { error } = await supabase.from('hunter_citebait_posts').delete().eq('id', id);
    if (!error) setPosts((prev) => prev.filter((p) => p.id !== id));
    setBusyId(null);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditUrl(p.url);
    setEditTopic(p.topic);
  };

  const saveEdit = async (id) => {
    const url = editUrl.trim();
    const topic = editTopic.trim();
    if (!url || !topic) return;
    setBusyId(id);
    const { error } = await supabase
      .from('hunter_citebait_posts')
      .update({ url, topic })
      .eq('id', id);
    if (!error) {
      setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, url, topic } : p)));
      setEditingId(null);
    }
    setBusyId(null);
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        Posts you've already shipped. Hunter checks every hit against this list (has this topic
        already been covered?) and the weekly digest uses it to flag duplicate topic candidates.
      </p>
      <div className={`${card} shadow-sm overflow-hidden`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3 w-2/5">URL</th>
              <th className="px-4 py-3">Topic</th>
              <th className="px-4 py-3 w-28 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {/* Inline add row */}
            <tr className="bg-gray-50/50">
              <td className="px-4 py-3">
                <input
                  className={`${input} w-full`}
                  placeholder="https://www.getsurfox.com/blog/…"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                />
              </td>
              <td className="px-4 py-3">
                <input
                  className={`${input} w-full`}
                  placeholder="One-line topic…"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addPost()}
                />
              </td>
              <td className="px-4 py-3 text-right">
                <button className={btnPrimary} disabled={saving || !newUrl.trim() || !newTopic.trim()} onClick={addPost}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add
                </button>
              </td>
            </tr>

            {loading ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td>
              </tr>
            ) : posts.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center text-gray-500">
                  No shipped posts yet. Add your first above.
                </td>
              </tr>
            ) : (
              posts.map((p) => {
                const editing = editingId === p.id;
                const busy = busyId === p.id;
                return (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 max-w-xs">
                      {editing ? (
                        <input
                          className={`${input} w-full`}
                          value={editUrl}
                          onChange={(e) => setEditUrl(e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 truncate"
                        >
                          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{p.url}</span>
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editing ? (
                        <input
                          className={`${input} w-full`}
                          value={editTopic}
                          onChange={(e) => setEditTopic(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveEdit(p.id)}
                        />
                      ) : (
                        <span className="text-gray-800">{p.topic}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {editing ? (
                          <>
                            <IconBtn title="Save" onClick={() => saveEdit(p.id)} busy={busy}>
                              <Check className="w-4 h-4 text-green-600" />
                            </IconBtn>
                            <IconBtn title="Cancel" onClick={() => setEditingId(null)}>
                              <X className="w-4 h-4 text-gray-400" />
                            </IconBtn>
                          </>
                        ) : (
                          <>
                            <IconBtn title="Edit" onClick={() => startEdit(p)}>
                              <Pencil className="w-4 h-4 text-gray-400" />
                            </IconBtn>
                            <IconBtn title="Delete" onClick={() => deletePost(p.id)} busy={busy}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </IconBtn>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IconBtn({ children, title, onClick, busy }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={busy}
      className="p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : children}
    </button>
  );
}
